import { yupResolver } from '@hookform/resolvers/yup';
import { CountryCode } from 'libphonenumber-js';
import { useTranslation } from 'next-i18next';
import {
  Control,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import toast from 'react-hot-toast';
import { useCookies } from 'utils/general/storage';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import {
  useAddTeamMember,
  useEditTeamMember,
  useGetTeamRoles,
} from '@/modules/settings/Team/apiHooks';
import { AddTeamMemeberSuccessModal } from '@/modules/settings/Team/components/AddEditTeamMember/AddTeamMemeberSuccessModal';
import { DowngradeConfirmationModal } from '@/modules/settings/Team/components/AddEditTeamMember/DowngradeConfirmationModal';
import { useCompanyTeamSettings } from '@/modules/settings/Team/components/logic';
import { ICompanyTeamsSettings } from '@/modules/settings/Team/types';
import {
  Button,
  InputField,
  PhoneInputWithCountry,
  SelectField,
  Typography,
} from '@/shared/components/atoms';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import { roles } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { IGeneral } from '@/shared/types';

const AddEditTeamMember: React.FC<{
  formName: string;
  memberId?: number;
  isBillingUser?: boolean;
  initialValues?: ICompanyTeamsSettings.IAddTeamMemberFormValues;
  onSuccess?: () => void;
}> = ({ memberId, initialValues, isBillingUser, onSuccess, formName }) => {
  const { t } = useTranslation();

  const { openModal, closeModal } = useModal();

  const { user } = useAuth();
  const { getCookie } = useCookies();

  const [selectedAsset] = useStore((store) => store?.asset);
  const [assets] = useStore((store) => store?.assetsList);

  const assetsList = assets
    ? [...assets.farms, ...assets.packhouses].map((asset) => ({
        label: asset.name,
        value: asset.id,
      }))
    : undefined;

  const { AddTeamMemberSchema, checkAccessLoss } = useCompanyTeamSettings();

  const formMethods = useForm<ICompanyTeamsSettings.IAddTeamMemberFormValues>({
    resolver: yupResolver(AddTeamMemberSchema),
    defaultValues: {
      ...initialValues,
      countryCode:
        getCookie('defaultCountryCode') ?? selectedAsset?.country ?? 'RU',
    },
    mode: 'all',
  });

  const {
    formState: { errors, isValid },
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
  } = formMethods;

  const selectedRole = watch('roleId');

  const { data: rolesList } = useGetTeamRoles();
  const {
    mutate: addTeamMemberMutation,
    isLoading: isAddingTeamMemberLoading,
  } = useAddTeamMember();
  const {
    mutate: editTeamMemberMutation,
    isLoading: isEditingTeamMemberLoading,
  } = useEditTeamMember();

  const onSubmit: SubmitHandler<
    ICompanyTeamsSettings.IAddTeamMemberFormValues
  > = (values) => {
    if (user && user.companyId) {
      const mutationValues: ICompanyTeamsSettings.IAddTeamMemberDTO = {
        ...values,
        assignedProperties:
          selectedRole === roles.INVENTORY_SPECIALIST
            ? values.assignedProperties
            : undefined,
        companyId: user.companyId,
      };

      if (memberId) {
        const handleEditConfirmation = () => {
          editTeamMemberMutation(
            {
              id: memberId,
              obj: mutationValues,
            },
            {
              onSuccess: () => {
                toast.success(t('team-member-edited-successfully'));
                closeModal();
                closeModal();
              },
            }
          );
        };

        if (initialValues && initialValues.roleId !== mutationValues.roleId) {
          const {
            isLossingFieldAppAccess,
            isLossingAydiEyeAccess,
            isLossingManagerAppAccess,
          } = checkAccessLoss(initialValues.roleId, mutationValues.roleId);
          if (
            isLossingAydiEyeAccess ||
            isLossingFieldAppAccess ||
            isLossingManagerAppAccess
          ) {
            openModal(
              <DowngradeConfirmationModal
                isLossingFieldAppAccess={isLossingFieldAppAccess}
                handleConfirmation={handleEditConfirmation}
              />,
              {
                dismissable: false,
                containerClassName: 'max-w-md',
              }
            );
          } else handleEditConfirmation();
        } else handleEditConfirmation();
      } else
        addTeamMemberMutation(mutationValues, {
          onSuccess: () => {
            if (onSuccess) {
              onSuccess();
              return;
            }

            openModal(
              <AddTeamMemeberSuccessModal
                role={values.roleId}
                addAnotherFunction={() => {
                  reset();
                  closeModal();
                }}
              />,
              {
                containerClassName: 'max-w-md',
                dismissable: false,
              }
            );
          },
        });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form
        name={formName}
        onSubmit={handleSubmit(onSubmit)}
        className='relative flex flex-col gap-32 overflow-x-hidden tablet:min-w-[730px]'
      >
        <div className='mx-auto mt-32 flex w-11/12 max-w-full flex-col gap-16 tablet:w-1/2 desktop:max-w-[40%]'>
          <div className='flex flex-col gap-1'>
            <Typography weight='semiBold' variant='h6'>
              {t(memberId ? 'edit-team-member' : 'invite-team-member')}
            </Typography>
            {memberId ? (
              <></>
            ) : (
              <Typography className='text-surface-10' variant='caption'>
                {t('invite-team-member-description')}
              </Typography>
            )}
          </div>
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-16'
            label={t('First Name')}
            placeholder={t('First Name')}
            error={!!errors.firstName}
            errorMessage={errors.firstName?.message}
            className='!rounded-xl bg-background-transparent'
            disabled={isAddingTeamMemberLoading || isEditingTeamMemberLoading}
            {...register('firstName', {
              setValueAs: (value) => (value?.length ? value : undefined),
            })}
          />
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-16'
            label={t('Last Name')}
            placeholder={t('Last Name')}
            error={!!errors.lastName}
            errorMessage={errors.lastName?.message}
            className='!rounded-xl bg-background-transparent'
            disabled={isAddingTeamMemberLoading || isEditingTeamMemberLoading}
            {...register('lastName', {
              setValueAs: (value) => (value?.length ? value : undefined),
            })}
          />
          <SelectField<ICompanyTeamsSettings.ITeamMemberRole, false>
            id='roleId'
            getLabel={(role: ICompanyTeamsSettings.ITeamMemberRole) =>
              t(role.name)
            }
            getValue={(role: ICompanyTeamsSettings.ITeamMemberRole) => role.id}
            isDisabled={isAddingTeamMemberLoading || isEditingTeamMemberLoading}
            className='!rounded-xl bg-background-transparent'
            borderColor={themeColors().outline.variant}
            label={t('Role')}
            placeholder={t('Role')}
            list={rolesList}
            py='10px'
            px='0.5rem'
            borderRadius='1rem'
            width='w-full'
            customOption={(option) => (
              <div>
                <Typography weight='bold'>{t(option.data.name)}</Typography>
                <Typography variant='subtitle2'>
                  {t(option.data.description)}
                </Typography>
              </div>
            )}
          />
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-16'
            label={`${t('Job Title')} (${t('Recommended')})`}
            placeholder={t('Job Title')}
            error={!!errors.jobTitle}
            errorMessage={errors.jobTitle?.message}
            className='!rounded-xl bg-background-transparent'
            disabled={isAddingTeamMemberLoading || isEditingTeamMemberLoading}
            {...register('jobTitle', {
              setValueAs: (value) => (value?.length ? value : undefined),
            })}
          />
          <div>
            <PhoneInputWithCountry
              defaultCountry={getValues('countryCode') as CountryCode}
              onCountryChange={(country) => setValue('countryCode', country)}
              borderColor='border-outline-variant border-xs rounded-xl'
              py='py-[7px]'
              width='w-full'
              label={t('Mobile Number')}
              placeholder={t('Mobile Number')}
              id='phoneNumber'
              name='phoneNumber'
              error={!!errors.phoneNumber}
              errorMessage={errors.phoneNumber?.message}
              control={control as unknown as Control<FieldValues>}
              disabled={isAddingTeamMemberLoading || isEditingTeamMemberLoading}
            />
          </div>

          <TippyTooltip
            content={
              isBillingUser ? t('billing-user-email-edit-tooltip') : undefined
            }
          >
            <InputField
              borderColor='border-outline-variant'
              width='w-full'
              py='!py-16'
              label={`${t('Email')} (${t('Recommended')})`}
              placeholder={t('Email')}
              error={!!errors.email}
              errorMessage={errors.email?.message}
              className='!rounded-xl bg-background-transparent'
              disabled={
                isAddingTeamMemberLoading ||
                isEditingTeamMemberLoading ||
                isBillingUser
              }
              {...register('email', {
                setValueAs: (value) => (value?.length ? value : undefined),
              })}
            />
          </TippyTooltip>
          {selectedRole === roles.INVENTORY_SPECIALIST && (
            <SelectField
              id='assignedProperties'
              getLabel={(obj: IGeneral.LabelValuePair) => t(obj.label)}
              getValue={(obj: IGeneral.LabelValuePair) => obj.value}
              isMulti
              isDisabled={
                isAddingTeamMemberLoading || isEditingTeamMemberLoading
              }
              className='!rounded-xl bg-background-transparent'
              borderColor={themeColors().outline.variant}
              label={t('Assigned Properties')}
              placeholder={t('Assigned Properties')}
              list={assetsList}
              py='10px'
              px='0.5rem'
              borderRadius='1rem'
              width='w-full'
            />
          )}
          {!memberId && (
            <InputField
              borderColor='border-outline-variant'
              width='w-full'
              py='!py-16'
              label={`${t('Invitation Note')} (${t('Recommended')})`}
              placeholder={t('Invitation Note')}
              error={!!errors.invitationNote}
              errorMessage={errors.invitationNote?.message}
              className='!rounded-xl bg-background-transparent'
              disabled={isAddingTeamMemberLoading || isEditingTeamMemberLoading}
              {...register('invitationNote', {
                setValueAs: (value) => (value?.length ? value : undefined),
              })}
            />
          )}
        </div>
        <div className='flex w-full justify-end gap-8'>
          <Button
            type='submit'
            size='none'
            disabled={!isValid}
            className='flex h-56 w-216 items-center justify-center !rounded-xl'
          >
            {t(memberId ? 'save' : 'Send Invitation')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default AddEditTeamMember;
